/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useLocation, useNavigate } from 'js/lib/useRouter';

import { Button, Grid } from '@coursera/cds-core';

import AuthenticationV3CtaTracking from 'bundles/authentication/shared/AuthenticationV3CtaTracking';
import type { AuthButtonProps } from 'bundles/composable-page-header/types';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

const styles = {
  login: css`
    && {
      display: block;
      background: none;
      font-weight: normal;
      max-height: 56px;
      max-width: 120px;
      white-space: nowrap;
      padding: var(--cds-spacing-150);

      :hover {
        color: var(--cds-color-interactive-primary);
      }

      :focus {
        outline: 5px auto -webkit-focus-ring-color;
        outline-offset: -2px;
        box-shadow: none !important;
      }
    }
  `,
  signup: css`
    && {
      font-weight: 700;
      max-height: 56px;
      max-width: 120px;
      padding: var(--cds-spacing-100) var(--cds-spacing-150);
      text-decoration: none !important;
    }
  `,
};

type UnauthenticatedActionsProps = {
  buttons: AuthButtonProps[];
};

const UnauthenticatedActionsGridItem = ({ buttons }: UnauthenticatedActionsProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const getAuthQueryParams = (authMode: 'signup' | 'login') => {
    const queryParams = new URLSearchParams(location.query);
    queryParams.set('authMode', authMode);

    return queryParams;
  };

  const getAuthHref = (authMode: 'signup' | 'login') =>
    `${location.pathname}?${getAuthQueryParams(authMode).toString()}`;

  const handleAuthClick = (authMode: 'signup' | 'login') => {
    navigate({
      pathname: location.pathname,
      search: `?${getAuthQueryParams(authMode).toString()}`,
    });
  };

  return (
    <AuthenticationV3CtaTracking>
      {(trackV3) => (
        <React.Fragment>
          {buttons.map(({ label, authMode }) => (
            <Grid item key={`${authMode}_button_desktop`} wrap="nowrap">
              <Button
                css={authMode === 'signup' ? styles.signup : styles.login}
                href={getAuthHref(authMode)}
                variant={authMode === 'signup' ? 'secondary' : 'ghost'}
                size="small"
                component={TrackedLink2}
                trackingName="header_right_nav_button"
                textAlign="center"
                data-e2e={`header-${authMode}-button`}
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  trackV3({ name: authMode, sectionName: 'page_header' });
                  navigate(getAuthHref(authMode));
                  handleAuthClick(authMode);
                }}
              >
                {label}
              </Button>
            </Grid>
          ))}
        </React.Fragment>
      )}
    </AuthenticationV3CtaTracking>
  );
};

export default UnauthenticatedActionsGridItem;
